export type ImageUploadType =
  | 'PRODUCT_IMAGE'
  | 'CATEGORY_IMAGE'
  | 'BRAND_LOGO'
  | 'HOME_TAB_ICON'
  | 'BOTTOM_DYNAMIC_TAB_ART'
  | 'HOME_BANNER'
  | 'COLLECTION_SECTION_HEADER'
  | 'COLLECTION_TILE_ART'
  | 'COLLECTION_OFFER_ICON'
  | 'FEATURED_CARD'
  | 'PROMO_CARD_ART'
  | 'COLLECTION_ICON'
  | 'COLLECTION_PAGE_BANNER_COLLECTION'
  | 'COLLECTION_PAGE_BANNER_BRAND'
  | 'COLLECTION_PAGE_BANNER_CAMPAIGN'
  | 'LIVE_TRACKING_AD';

export type ImagePreviewKind = 'default' | 'home-tab' | 'bottom-tab' | 'featured-card' | 'section-header' | 'tracking-ad';

export interface ImageUploadConfig {
  type: ImageUploadType;
  label: string;
  recommendedWidth: number;
  recommendedHeight: number;
  minWidth: number;
  minHeight: number;
  maxFilesizeKb: number;
  allowedFormats: string[];
  targetRatio: number;
  ratioTolerance: number;
  note: string;
  safeArea: string;
  compositionGuidance: string;
  backgroundGuidance: string;
  previewKind: ImagePreviewKind;
  fitMode: 'contain' | 'cover';
  backgroundMode: 'transparent' | 'white' | 'preserve';
  preferredFormat: 'webp' | 'png' | 'jpeg';
  allowCrop: boolean;
  allowPadding: boolean;
  allowTransparency: boolean;
  mobileResizeMode: 'contain' | 'cover';
  compressionMode: 'lossless' | 'visually_lossless' | 'balanced';
  qualityTarget?: number;
  minQuality?: number;
}

export const SUPPORTED_WEB_IMAGES = ['image/jpeg', 'image/png', 'image/webp'];
export const TRANSPARENT_WEB_IMAGES = ['image/png', 'image/webp'];

const squareTransparent = {
  allowedFormats: TRANSPARENT_WEB_IMAGES,
  targetRatio: 1,
  fitMode: 'contain' as const,
  backgroundMode: 'transparent' as const,
  preferredFormat: 'png' as const,
  allowCrop: false,
  allowPadding: true,
  allowTransparency: true,
  mobileResizeMode: 'contain' as const,
  compressionMode: 'lossless' as const,
  backgroundGuidance: 'Transparent background recommended (PNG or alpha-capable WebP).',
  previewKind: 'default' as const,
};

const opaqueBanner = {
  allowedFormats: SUPPORTED_WEB_IMAGES,
  fitMode: 'cover' as const,
  backgroundMode: 'preserve' as const,
  preferredFormat: 'webp' as const,
  allowCrop: true,
  allowPadding: false,
  allowTransparency: false,
  mobileResizeMode: 'cover' as const,
  compressionMode: 'visually_lossless' as const,
  backgroundGuidance: 'Use an edge-to-edge background; transparency is not required.',
};

/** Canonical Admin upload contract. Customer ratios and resize modes mirror it. */
export const IMAGE_CONFIGS: Record<ImageUploadType, ImageUploadConfig> = {
  PRODUCT_IMAGE: {
    ...squareTransparent, type: 'PRODUCT_IMAGE', label: 'Product Image',
    recommendedWidth: 1200, recommendedHeight: 1200, minWidth: 600, minHeight: 600,
    maxFilesizeKb: 1024, allowedFormats: SUPPORTED_WEB_IMAGES, ratioTolerance: 0.03,
    note: 'Use a transparent or clean neutral background.',
    safeArea: 'Keep approximately 5–10% clear around the product.',
    compositionGuidance: 'Centre one product and let it occupy approximately 80–90% of the canvas.',
    preferredFormat: 'webp', compressionMode: 'visually_lossless',
  },
  CATEGORY_IMAGE: {
    ...squareTransparent, type: 'CATEGORY_IMAGE', label: 'Category Image',
    recommendedWidth: 512, recommendedHeight: 512, minWidth: 256, minHeight: 256,
    maxFilesizeKb: 512, ratioTolerance: 0.02,
    note: 'Use one clear category-specific object without text.',
    safeArea: 'Keep approximately 8–12% clear at every edge.',
    compositionGuidance: 'Centre the object and let it occupy approximately 70–85% of the canvas.',
  },
  BRAND_LOGO: {
    ...squareTransparent, type: 'BRAND_LOGO', label: 'Brand Logo',
    recommendedWidth: 600, recommendedHeight: 600, minWidth: 256, minHeight: 256,
    maxFilesizeKb: 512, ratioTolerance: 0.02,
    note: 'The canvas must be square; the logo artwork may retain its natural shape.',
    safeArea: 'Keep the natural logo inside approximately 75–85% of the square canvas.',
    compositionGuidance: 'Centre the logo without stretching or distorting it.',
  },
  HOME_TAB_ICON: {
    ...squareTransparent, type: 'HOME_TAB_ICON', label: 'Home Tab Icon',
    recommendedWidth: 256, recommendedHeight: 256, minWidth: 128, minHeight: 128,
    maxFilesizeKb: 256, ratioTolerance: 0.02,
    note: 'Use one category-specific object. No text, border, decorative background, or wide empty canvas.',
    safeArea: 'Keep approximately 10–12% clear at every edge; the app already adds its own inset.',
    compositionGuidance: 'Centre the object and let it occupy approximately 70–80% of the canvas.',
    previewKind: 'home-tab',
  },
  BOTTOM_DYNAMIC_TAB_ART: {
    ...squareTransparent, type: 'BOTTOM_DYNAMIC_TAB_ART', label: 'Bottom Dynamic Tab Artwork',
    recommendedWidth: 500, recommendedHeight: 200, minWidth: 250, minHeight: 100,
    maxFilesizeKb: 256, targetRatio: 2.5, ratioTolerance: 0.03,
    mobileResizeMode: 'cover',
    note: 'Upload complete button artwork, including its background and text. The image fills the tile.',
    safeArea: 'Keep approximately 5–8% clear around the artwork.',
    compositionGuidance: 'Fill the horizontal canvas naturally; do not place a small square icon on a wide empty canvas.',
    previewKind: 'bottom-tab',
  },
  HOME_BANNER: {
    ...opaqueBanner, type: 'HOME_BANNER', label: 'Home Banner',
    recommendedWidth: 1260, recommendedHeight: 540, minWidth: 840, minHeight: 360,
    maxFilesizeKb: 1024, targetRatio: 21 / 9, ratioTolerance: 0.03,
    note: 'Use a full-bleed banner; do not upload a square source.',
    safeArea: 'Keep critical content approximately 8–10% away from every edge.',
    compositionGuidance: 'If CMS copy overlays the left side, keep the important visual subject toward the centre or right.',
    previewKind: 'section-header',
  },
  COLLECTION_SECTION_HEADER: {
    ...opaqueBanner, type: 'COLLECTION_SECTION_HEADER', label: 'Collection Section Header',
    recommendedWidth: 1200, recommendedHeight: 310, minWidth: 800, minHeight: 207,
    maxFilesizeKb: 1024, targetRatio: 1200 / 310, ratioTolerance: 0.03,
    note: 'Use a short, full-bleed header rather than tall artwork.',
    safeArea: 'Keep critical subject and text-safe content inside the centre 80%.',
    compositionGuidance: 'Extend the background to every edge so controlled cover cropping remains safe.',
    previewKind: 'section-header',
  },
  COLLECTION_TILE_ART: {
    ...squareTransparent, type: 'COLLECTION_TILE_ART', label: 'Collection Tile Artwork',
    recommendedWidth: 800, recommendedHeight: 800, minWidth: 400, minHeight: 400,
    maxFilesizeKb: 768, ratioTolerance: 0.03,
    note: 'Use one reusable centred subject with no baked-in text.',
    safeArea: 'Keep important content away from the edges so the asset survives different tile layouts.',
    compositionGuidance: 'Use transparent art for labeled layouts; use edge-to-edge photography only for intentional photo layouts.',
    preferredFormat: 'webp', compressionMode: 'visually_lossless',
  },
  COLLECTION_OFFER_ICON: {
    ...squareTransparent, type: 'COLLECTION_OFFER_ICON', label: 'Collection Offer Icon',
    recommendedWidth: 256, recommendedHeight: 256, minWidth: 128, minHeight: 128,
    maxFilesizeKb: 256, ratioTolerance: 0.02,
    note: 'Use one small, clear offer object with no text.',
    safeArea: 'Keep approximately 8–12% clear at every edge.',
    compositionGuidance: 'Centre the object and let it occupy approximately 75–85% of the canvas.',
  },
  FEATURED_CARD: {
    ...opaqueBanner, type: 'FEATURED_CARD', label: 'Featured Card',
    recommendedWidth: 1200, recommendedHeight: 680, minWidth: 600, minHeight: 340,
    maxFilesizeKb: 1024, targetRatio: 1200 / 680, ratioTolerance: 0.03,
    note: 'Do not bake title, subtitle, or CTA text into the image.',
    safeArea: 'Reserve approximately the left 58–60% for app-rendered copy.',
    compositionGuidance: 'Place the main visual subject primarily on the right.',
    previewKind: 'featured-card',
  },
  PROMO_CARD_ART: {
    ...squareTransparent, type: 'PROMO_CARD_ART', label: 'Promo Card Artwork',
    recommendedWidth: 800, recommendedHeight: 800, minWidth: 400, minHeight: 400,
    maxFilesizeKb: 512, ratioTolerance: 0.03,
    note: 'Use a transparent product cut-out or visual subject with no baked-in text.',
    safeArea: 'Keep approximately 8–12% clear at every edge.',
    compositionGuidance: 'Centre the subject and let it occupy approximately 75–85% of the canvas.',
    preferredFormat: 'webp', compressionMode: 'visually_lossless',
  },
  COLLECTION_ICON: {
    ...squareTransparent, type: 'COLLECTION_ICON', label: 'Collection Icon',
    recommendedWidth: 512, recommendedHeight: 512, minWidth: 256, minHeight: 256,
    maxFilesizeKb: 512, ratioTolerance: 0.02,
    note: 'Use one clear collection symbol without text.',
    safeArea: 'Keep approximately 8–12% clear at every edge.',
    compositionGuidance: 'Centre the object on a square transparent canvas.',
  },
  COLLECTION_PAGE_BANNER_COLLECTION: {
    ...opaqueBanner, type: 'COLLECTION_PAGE_BANNER_COLLECTION', label: 'Collection Page Banner',
    recommendedWidth: 1200, recommendedHeight: 649, minWidth: 740, minHeight: 400,
    maxFilesizeKb: 1024, targetRatio: 1200 / 649, ratioTolerance: 0.03,
    note: 'Standard collection-page artwork.', safeArea: 'Keep critical content inside the centre 80%.',
    compositionGuidance: 'Use edge-to-edge imagery and avoid important details beneath the top navigation controls.',
    previewKind: 'section-header',
  },
  COLLECTION_PAGE_BANNER_BRAND: {
    ...opaqueBanner, type: 'COLLECTION_PAGE_BANNER_BRAND', label: 'Brand Page Banner',
    recommendedWidth: 1200, recommendedHeight: 558, minWidth: 774, minHeight: 360,
    maxFilesizeKb: 1024, targetRatio: 1200 / 558, ratioTolerance: 0.03,
    note: 'Wide brand-store artwork.', safeArea: 'Keep critical brand marks inside the centre 80%.',
    compositionGuidance: 'Avoid placing the brand mark beneath top navigation controls.',
    previewKind: 'section-header',
  },
  COLLECTION_PAGE_BANNER_CAMPAIGN: {
    ...opaqueBanner, type: 'COLLECTION_PAGE_BANNER_CAMPAIGN', label: 'Campaign Page Banner',
    recommendedWidth: 1200, recommendedHeight: 811, minWidth: 740, minHeight: 500,
    maxFilesizeKb: 1024, targetRatio: 1200 / 811, ratioTolerance: 0.03,
    note: 'Campaign artwork receives a title overlay at the bottom.',
    safeArea: 'Keep the top controls and bottom copy area free of critical visual details.',
    compositionGuidance: 'Place the main subject near the centre and extend the background to every edge.',
    previewKind: 'section-header',
  },
  LIVE_TRACKING_AD: {
    ...opaqueBanner, type: 'LIVE_TRACKING_AD', label: 'Live Tracking Advertisement',
    recommendedWidth: 1350, recommendedHeight: 500, minWidth: 810, minHeight: 300,
    maxFilesizeKb: 1024, targetRatio: 2.7, ratioTolerance: 0.03,
    note: 'Do not bake the CTA or campaign title into the image; the app renders copy below it.',
    safeArea: 'Keep important visual content inside the centre 75%.',
    compositionGuidance: 'Use a full-bleed image that remains readable with controlled cover cropping.',
    previewKind: 'tracking-ad',
  },
};
